import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { Eye, EyeOff } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { AccountShell } from '../components/account/AccountShell'
import { SettingsSection } from '../components/knowledge/settings/SettingsSection'
import { Button, Input, Text } from '../components/ui'
import { authClient } from '../lib/auth-client'

const profileSchema = z.object({
  name: z.string().min(1, 'Name is required').max(120, 'Name is too long'),
})

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(1, 'Confirm your new password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

type ProfileForm = z.infer<typeof profileSchema>
type PasswordForm = z.infer<typeof passwordSchema>

export const Route = createFileRoute('/personal-information')({
  beforeLoad: async () => {
    const { data: session } = await authClient.getSession()
    if (!session) {
      throw redirect({ to: '/login' })
    }
    return { session }
  },
  component: PersonalInformationPage,
})

function PersonalInformationPage() {
  const { session } = Route.useRouteContext()
  const [profileMessage, setProfileMessage] = useState<string | null>(null)
  const [profileError, setProfileError] = useState<string | null>(null)
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [hasCredentialAccount, setHasCredentialAccount] = useState<boolean | null>(null)

  const profileForm = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: { name: session.user.name ?? '' },
  })

  const passwordForm = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  })

  useEffect(() => {
    let cancelled = false

    async function loadAccounts() {
      const { data } = await authClient.listAccounts()
      if (cancelled) return
      setHasCredentialAccount(
        data?.some((account) => account.providerId === 'credential') ?? false,
      )
    }

    void loadAccounts()

    return () => {
      cancelled = true
    }
  }, [])

  async function onProfileSubmit(data: ProfileForm) {
    setProfileMessage(null)
    setProfileError(null)

    const { error } = await authClient.updateUser({ name: data.name.trim() })

    if (error) {
      setProfileError(error.message ?? 'Could not update your profile')
      return
    }

    setProfileMessage('Profile updated')
  }

  async function onPasswordSubmit(data: PasswordForm) {
    setPasswordMessage(null)
    setPasswordError(null)

    const { error } = await authClient.changePassword({
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
      revokeOtherSessions: true,
    })

    if (error) {
      setPasswordError(error.message ?? 'Could not change your password')
      return
    }

    passwordForm.reset()
    setPasswordMessage('Password updated')
  }

  return (
    <AccountShell>
      <div className="mx-auto w-full max-w-2xl">
        <div className="mb-8">
          <Text variant="h2" className="mb-1 text-2xl sm:text-3xl">
            Personal information
          </Text>
          <Text variant="body-sm" className="text-foreground/60">
            Manage your SonderSoft account profile and sign-in details.
          </Text>
        </div>

        <SettingsSection
          id="profile"
          title="Profile"
          description="Your name appears across workspaces and team views."
        >
          <form
            className="flex max-w-md flex-col gap-4"
            onSubmit={profileForm.handleSubmit(onProfileSubmit)}
          >
            <Input
              label="Full name"
              error={profileForm.formState.errors.name?.message}
              {...profileForm.register('name')}
            />

            <div className="flex flex-col gap-1.5">
              <Text as="label" variant="label">
                Email
              </Text>
              <div className="flex h-11 items-center rounded-full border border-foreground/10 bg-stone/50 px-4 text-sm text-foreground/70">
                {session.user.email}
              </div>
              <Text variant="caption" className="text-foreground/45">
                Email changes are not available yet. Contact support if you need help.
              </Text>
            </div>

            {profileError && (
              <Text variant="caption" className="text-red-600">
                {profileError}
              </Text>
            )}
            {profileMessage && (
              <Text variant="caption" className="text-foreground/60">
                {profileMessage}
              </Text>
            )}

            <Button
              type="submit"
              loading={profileForm.formState.isSubmitting}
              className="w-fit"
            >
              Save changes
            </Button>
          </form>
        </SettingsSection>

        <SettingsSection
          id="password"
          title="Password"
          description="Update the password you use to sign in with email."
        >
          {hasCredentialAccount === null ? (
            <div className="h-24 animate-pulse rounded-2xl bg-stone/60" />
          ) : hasCredentialAccount ? (
            <form
              className="flex max-w-md flex-col gap-4"
              onSubmit={passwordForm.handleSubmit(onPasswordSubmit)}
            >
              <Input
                label="Current password"
                type={showCurrentPassword ? 'text' : 'password'}
                autoComplete="current-password"
                error={passwordForm.formState.errors.currentPassword?.message}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword((value) => !value)}
                    className="text-foreground/50"
                    aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                  >
                    {showCurrentPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                }
                {...passwordForm.register('currentPassword')}
              />
              <Input
                label="New password"
                type={showNewPassword ? 'text' : 'password'}
                autoComplete="new-password"
                error={passwordForm.formState.errors.newPassword?.message}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowNewPassword((value) => !value)}
                    className="text-foreground/50"
                    aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                  >
                    {showNewPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                }
                {...passwordForm.register('newPassword')}
              />
              <Input
                label="Confirm new password"
                type={showNewPassword ? 'text' : 'password'}
                autoComplete="new-password"
                error={passwordForm.formState.errors.confirmPassword?.message}
                {...passwordForm.register('confirmPassword')}
              />

              {passwordError && (
                <Text variant="caption" className="text-red-600">
                  {passwordError}
                </Text>
              )}
              {passwordMessage && (
                <Text variant="caption" className="text-foreground/60">
                  {passwordMessage}
                </Text>
              )}

              <Button
                type="submit"
                loading={passwordForm.formState.isSubmitting}
                className="w-fit"
              >
                Update password
              </Button>
            </form>
          ) : (
            <Text variant="body-sm" className="text-foreground/60">
              You sign in with Google. Password management is handled by your Google account.
            </Text>
          )}
        </SettingsSection>
      </div>
    </AccountShell>
  )
}
